import React, {FC, useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../../icons";
import { Button, ModalVariables, StartPage, SearchComponent, FilterBar} from "../../../components";
import Illustration from './illustration.svg'
import IllustrationDark from './illustration-dark.svg'
import { useNavigate } from "react-router";
import { useAuth } from "../../../context/user.context";
import { useUi } from "../../../context/ui.context";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { loadBalancerService } from "../../../api";
import { GreenDot, StateWrapper, Greentext } from "../Instances/styles";
import { handleStatusName } from "../../../utils/handle-status-name";
import rootConfig from "../../../config";
import { compareArrays } from "../../../utils/findIntersection";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { usePageTitle } from "../../../context/navigationContext";

const LB_FILTER_KEYS = [
  'operating_status',
  'provisioning_status',
  'vip_network',
  'project__service__client',
  'region',
  'created_at',
];
const DEFAULT_ORDERING = '-created_at';

const LoadBalancers:FC = () => {
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);

  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [paginationData, setPaginationData] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [modalType, setModalType] = useState<any>('')
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [instanceId, setInstanceId] = useState<any>()
  const [checked, setChecked] = useState<any[]>([])
  const [alertTitle, setAlertTitle] = useState<any>()
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [loadBalancers, setLoadBalancers] = useState<any[]>([])
  const [deletedBalancer, setDeletedBalancer] = useState<any>()
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    if (!filterOptions) return undefined;
    return LB_FILTER_KEYS.reduce((acc:any, key) => {
      if (filterOptions?.[key]?.length) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {});
  }, [filterOptions]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }
  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(loadBalancers, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);
  const [t] = useTranslation()
  const navigate = useNavigate()
  const {
    setViewModalComplete,
    setActiveAnimationComplete,
    viewAlert,
  } = useUi()
  const {user} = useAuth()
  const _createFunc = () => {
    navigate('create')
  }
  const _updateLoadBalancers = () => {
    loadBalancerService.getList({
      page:page||1,
      ordering: ordering || DEFAULT_ORDERING,
      page_size:pageSize||10,
      active_client:user?.user?.clients?.[0]?.id,
      search:searchString||'',
      filtering: filtering || '',
    })
    .then((res:any) => {
      setFilterOptions(res?.data?.filter_options)
      setPaginationData(res.data)
      setLoadBalancers(res.data.objects.map((balancer:any) => {
        return{
          ...balancer,
          navigate:balancer.id,
          name:balancer.name || balancer.id,
          created_at: timeConverterMMDDYYHHMM(balancer.created_at),
          region: balancer.region,
          status: 
            (<StateWrapper>
              <GreenDot active={balancer.operating_status}/>
              <Greentext active={balancer.operating_status}>
                {handleStatusName(balancer.operating_status, t)}
              </Greentext>
            </StateWrapper>) ,
          provisioning_status: 
            (<StateWrapper>
              <GreenDot active={balancer.provisioning_status}/>
              <Greentext active={balancer.provisioning_status}>
                {handleStatusName(balancer.provisioning_status, t)}
              </Greentext>
            </StateWrapper>),
        }
      }))
    })
    .catch((e) => {
      console.log('e',e);
    })
    .finally(() => {
      setPageLoading(false)
      // setLoading(false)
    })
  }
  const _deleteBalancers = async () => {
    // setPageLoading(true)
    if(allChecked) {
      let promises:any[] = []
      await loadBalancers.map(async(item) => {
        let promise = loadBalancerService.deleteBalancer(item.id)
        promises.push(promise)
      })
      await Promise.all(promises)
      .then((res) => {
        _updateLoadBalancers()
        setAllChecked(false)
        setViewModal(false)
        setActiveAnimation(false)
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
    } else if (checked?.length > 0){
      let promises:any[] = []
      await checked.map(async(item) => {
        let promise = loadBalancerService.deleteBalancer(item.id)
        promises.push(promise)
      })
      await Promise.all(promises)
      .then((res) => {
        _updateLoadBalancers()
        setChecked([])
        setViewModal(false)
        setActiveAnimation(false)
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
    } else if (deletedBalancer) {
      loadBalancerService.deleteBalancer(deletedBalancer.id)
      .then((res) => {
        _updateLoadBalancers()
        setViewModal(false)
        setActiveAnimation(false)
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
    }
  }
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const closeModal = (values:any) => {
    if(modalType === 'delete'){
      setLoading(true);
      _deleteBalancers()
    } else if (modalType === 'add-ip-to-balancer') {
      setLoading(true);
      loadBalancerService.associateFloatingIp(instanceId.id, values)
      .then((res) => {
        _updateLoadBalancers()
        setViewModal(false)
        setActiveAnimation(false)
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
    } else if (modalType === 'universal'){
      setLoading(true);
      if (alertTitle === `${t('DISSOCIATE_IP_TITLE')}`) {
        loadBalancerService.dissociateFloatingIp(instanceId)
        .then((res) => {
          _updateLoadBalancers()
          setViewModal(false)
          setActiveAnimation(false)
        })
        .catch((e) => {
          if(e?.response?.data.detail){
            viewAlert({
              severity:'error',
              message:e.response.data.detail,
            })
          }
        })
        .finally(() => {
          setLoading(false)
        })
      }
    } else {
      setViewModal(false)
      setActiveAnimation(false)
    }
  }

  useEffect(() => {
    _updateLoadBalancers()
    const update = setInterval(() => {
      _updateLoadBalancers()
    },6000)
    return () => {
      clearInterval(update)
    }
  },[page, ordering, pageSize, searchString, filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  const Columns = [
    {
      title:t("NAME"),
      key:'name',
      sorted:true,
    },
    {
      title:t("OPERATING_STATUS"),
      key:'status'
    },
    {
      title:t("IP_ADDRESS"),
      key:'vip_address'
    },
    {
      title:t("PROVISIONING_STATUS"),
      key:'provisioning_status'
    },
    {
      title:t("REGION"),
      key:'region',
      sorted:true,
    },
    {
      title:t("CREATED_AT"),
      key:'created_at',
      sorted:true,
    },
    {
      title:'',
      key:'props'
    },
  ]
  const items = [
    {
      title:t("EDIT"),
      id:'2',
      icon:icons.Edit,
      onClick: (id:any) => navigate(`edit/${id.id}`),
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      onClick:(id:any) => {
        openModal('delete')
        setAlertTitle(t("LOAD_BALANCER"))
        setAlertSubTitle([id])
        setDeletedBalancer(id)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  return (
    <>
      <StartPage
        title={`${t("LOAD_BALANCERS")}`}
        noResourceText={`${t("YOU_HAVE_NO_LOAD_BALANCERS")}`}
        createResourceButton={`${t('CREATE_LOAD_BALANCER')}`}
        createResourceButtonEventOrLink={_createFunc}
        haveResource={loadBalancers.length > 0}
        pageLoading={pageLoading}
        learnMoreAboutResource={`${t('LEARN_MORE_LOAD_BALANCERS')}`}
        howCreateVideoText={`${t('HOW_CREATE_LOAD_BALANCER')}`}
        learnMoreAboutResourceLink={`${rootConfig.docs_url}category/load-balancers`}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <Button
                title={t('CREATE_LOAD_BALANCER')}
                onClick={_createFunc}
                variant='primary'
                size='custom'
                customStyles={{height:'40px', whiteSpace:'nowrap'}}
              />
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                <Button
                  onClick={() => {
                    setAlertTitle(t("LOAD_BALANCERS"))
                    setAlertSubTitle(allChecked ? loadBalancers : checked)
                    openModal('delete')
                  }}
                  variant='icon'
                  size='display'
                  test_id='button-delete'
                  disabled={!allChecked && checked?.length === 0}
                >
                  <img src={icons.DeleteIcon}/>
                </Button>
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        videoImage={Illustration}
        videoImageDark={IllustrationDark}
        tableData={{
          columns:Columns,
          data:loadBalancers,
          paginationData:paginationData,
          allChecked:allChecked,
          props:items,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked,
          updateData:_updateLoadBalancers,
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        // dataToEdit={instance}
        instanceId={instanceId}
        // instance={instance}
        loading={loading}
      />
    </>
  )
}


export default LoadBalancers
